import React from 'react'
import { ObservatoryRow, ObservatoryType, ObservatoryValue } from 'common/types'
import * as api from 'common/api'
import * as config from 'common/config'
import dayjs from 'dayjs'
import { RainStatus, StageAlertLevel, StageForecastHours, ForecastStages, PumpAlertLevel, UnderpassStatus } from 'common/consts'
import * as utils from 'common/utils'

type StageForecastData = {
  stage: ObservatoryRow
  timeSeries: ObservatoryValue[]
}

type ObservatoryContextType = {
  observatories: ObservatoryRow[]
  setObservatories: React.Dispatch <React.SetStateAction<ObservatoryRow[]>>

  selected: ObservatoryRow | null
  setSelected: React.Dispatch <React.SetStateAction<ObservatoryRow | null>>

  timeSeries: ObservatoryValue[]
  setTimeSeries: React.Dispatch <React.SetStateAction<ObservatoryValue[]>>

  stageForecastData: StageForecastData[]
  setStageForecastData: React.Dispatch <React.SetStateAction<StageForecastData[]>>

  fetch: () => Promise<void>
  updateStatus: (date: dayjs.Dayjs) => Promise<void>
  getObservatory: (type: number, id: string) => ObservatoryRow | null
}

export const ObservatoryContext = React.createContext<ObservatoryContextType | undefined>(undefined)

export const fetchObservatories = async (): Promise<ObservatoryRow[]> => {
  const res = await api.get('/observatories', {
    domain: config.domain
  })
  if (res === null || !res.observatories) {
    return []
  } else {
    return res.observatories.map((obs: ObservatoryRow) => {
      if (obs.type === ObservatoryType.Suijin && obs.sensors) {
        const arr = [...obs.sensors]
        arr.sort((a, b) => b.level - a.level)
        obs.sensors = arr
      }
      return obs
    })
  }
}

type ObservatoryStatusResponse = {
  rains: {[key: string]: ObservatoryValue}
  stages: {[key: string]: ObservatoryValue}
  pumps: {[key: string]: ObservatoryValue}
  suijins: {[key: string]: ObservatoryValue}
  underpasses: {[key: string]: ObservatoryValue}
}

export const fetchObservatoriesStatus = async (date: dayjs.Dayjs): Promise<ObservatoryStatusResponse> => {
  const res = await api.get('/observatories/status/v2', {
    domain: config.domain,
    date: date.format('YYYY/MM/DD HH:mm')
  })
  const data = {
    stages: {},
    rains: {},
    pumps: {},
    suijins: {},
    underpasses: {},
  }
  if (res !== null) {
    if (res.stages) {
      data.stages = res.stages
    }
    if (res.rains) {
      data.rains = res.rains
    }
    if (res.pumps) {
      data.pumps = res.pumps
    }
    if (res.suijins) {
      data.suijins = res.suijins
    }
    if (res.underpasses) {
      data.underpasses = res.underpasses
    }
  }
  return data
}

export const ObservatoryProvider = ({children}: React.PropsWithChildren) => {
  const [observatories, setObservatories] = React.useState<ObservatoryRow[]>([])
  const [selected, setSelected] = React.useState<ObservatoryRow | null>(null)
  const [timeSeries, setTimeSeries] = React.useState<ObservatoryValue[]>([])
  const [stageForecastData, setStageForecastData] = React.useState<StageForecastData[]>([])

  const loadStatus = (targets: ObservatoryRow[], statuses: ObservatoryStatusResponse) => {
    const rains = statuses.rains
    const stages = statuses.stages
    const pumps = statuses.pumps
    const suijins = statuses.suijins
    const underpasses = statuses.underpasses
    targets.forEach(obs => {
      obs.status = ''
      if (obs.type === ObservatoryType.Rain) {
        obs.status = RainStatus.DEFICIT.key
        delete obs.min10
        delete obs.total
        if (rains[obs.id] === undefined) return
        if (rains[obs.id].total !== undefined) {
          obs.total = rains[obs.id].total
        }
        const min10 = rains[obs.id].min10
        if (min10 === undefined) return
        obs.min10 = min10
        if (min10 >= 20) {
          obs.status = RainStatus.VAL4.key
        } else if (min10 >= 10) {
          obs.status = RainStatus.VAL3.key
        } else if (min10 >= 5) {
          obs.status = RainStatus.VAL2.key
        } else if (min10 >= 1) {
          obs.status = RainStatus.VAL1.key
        } else if (min10 >= 0) {
          obs.status = RainStatus.VAL0.key
        }
      } else if (obs.type === ObservatoryType.Stage) {
        obs.status = StageAlertLevel.DEFICIT.key
        delete obs.level
        if (stages[obs.id] === undefined) return
        const level = stages[obs.id].level
        obs.level = level
        obs.status = utils.getStageStatus(obs, level)
      } else if (obs.type === ObservatoryType.Pump) {
        obs.status = PumpAlertLevel.DEFICIT.key
        delete obs.level
        if (pumps[obs.id] === undefined) return
        const level = pumps[obs.id].level_river
        obs.level = level
        obs.status = PumpAlertLevel.NORMAL.key
      } else if (obs.type === ObservatoryType.Suijin) {
        obs.status = 'maintenance'
        obs.level = undefined
        if (suijins[obs.id] !== undefined) {
          obs.status = 'waiting'
          const level = suijins[obs.id].level
          if (level !== undefined && level !== null && obs.sensors && obs.sensors.length > 0) {
            const sensorIndex = obs.sensors.findIndex(sensor => sensor.level <= level)
            if (sensorIndex === 0) {
              obs.status = 'flood4'
            } else if (sensorIndex === 1) {
              obs.status = 'flood3'
            } else if (sensorIndex === 2) {
              obs.status = 'flood2'
            } else if (sensorIndex === 3) {
              obs.status = 'flood1'
            }
            obs.level = level
          }
        }
      } else if (obs.type === ObservatoryType.Underpass) {
        obs.status = UnderpassStatus.DEFICIT.key
        delete obs.level
        delete obs.water_level_state
        delete obs.pumps
        if (underpasses[obs.id] === undefined) return
        const underpassData = underpasses[obs.id]
        if (underpassData.level !== undefined) {
          obs.level = underpassData.level
        }
        if (underpassData.water_level_state !== undefined) {
          const waterLevelState = underpassData.water_level_state
          Object.values(UnderpassStatus).forEach(status => {
            if (waterLevelState === status.label) {
              obs.status = status.key
            }
          })
        }
        if (underpassData.water_level_state !== undefined) {
          obs.water_level_state = underpassData.water_level_state
        }
        if (underpassData.pumps !== undefined) {
          obs.pumps = underpassData.pumps
        }
        if (underpassData.update_date !== undefined) {
          obs.update_date = underpassData.update_date
        }
        if (underpassData.power_state !== undefined) {
          obs.power_state = underpassData.power_state
        }
        if (underpassData.alert_mode !== undefined) {
          obs.alert_mode = underpassData.alert_mode
        }
        if (underpassData.communication_state !== undefined) {
          obs.communication_state = underpassData.communication_state
        }
      }
    })
  }

  const loadStageForecastData = async (targets: ObservatoryRow[], date: dayjs.Dayjs) => {
    if (config.isPublicMode || config.isYosokuMode) return
    if (targets.length === 0) {
      return
    }
    const targetDate = date.subtract(date.minute() % 5, 'minutes')
    const limitDate = dayjs.utc(date, 'YYYY/MM/DD HH:mm').add(StageForecastHours, 'hours').format('YYYY/MM/DD HH:mm')
    const res: { [key: string] : {stage: ObservatoryRow, timeSeries: ObservatoryValue[]} } = {}
    const processes = ForecastStages.map((osid: string) => {
      return (async () => {
        const stage = targets.find(obs => obs.type === ObservatoryType.Stage && obs.id === osid)
        if (stage === undefined) return
        let timeSeries = await utils.fetchObservatoryTimeSeries(stage, targetDate.format('YYYY/MM/DD HH:mm'))
        timeSeries = timeSeries
          .filter(ts => ts.date <= limitDate)
          .map(ts => {
            const jst = dayjs.utc(ts.date).local().format('YYYY/MM/DD HH:mm')
            return {...ts, date: jst}
          })
        res[osid] = {stage, timeSeries}
      })()
    })
    await Promise.all(processes)

    const data = []
    for (const osid of ForecastStages) {
      if (res[osid] !== undefined) {
        data.push(res[osid])
      }
    }

    setStageForecastData(data)
  }

  const fetch = async (): Promise<void> => {
    if (observatories.length > 0) {
      return
    }
    const date = dayjs.utc()
    const targets = await fetchObservatories()
    const statuses = await fetchObservatoriesStatus(date)
    loadStatus(targets, statuses)
    await loadStageForecastData(targets, date)
    setObservatories(targets)
  }

  const updateStatus = async (date: dayjs.Dayjs): Promise<void> => {
    if (observatories.length === 0) {
      return
    }
    const targets = [...observatories]
    const statuses = await fetchObservatoriesStatus(date)
    loadStatus(targets, statuses)
    await loadStageForecastData(targets, date)
    setObservatories(targets)
  }

  const getObservatory = (type: number, id: string): ObservatoryRow | null => {
    const target = observatories.find(obs => obs.type === type && obs.id === id)
    return target === undefined ? null : target
  }

  const value = React.useMemo(() => ({
    observatories, setObservatories,
    selected, setSelected,
    timeSeries, setTimeSeries,
    stageForecastData, setStageForecastData,
    fetch,
    getObservatory,
    updateStatus,
  }), [
    observatories, setObservatories,
    selected, setSelected,
    timeSeries, setTimeSeries,
    stageForecastData, setStageForecastData,
    fetch,
    getObservatory,
    updateStatus,
  ])

  return (
    <ObservatoryContext.Provider value={value}>
      {children}
    </ObservatoryContext.Provider>
  )
}
